import {
  comboDeposit,
  comboPrice,
  COUPON_CATEGORIES,
  isCombo,
  productPriceWithoutDeposit
} from "@/utils/pricing";
import currency from "currency.js";
import { cloneDeep } from "lodash";
import store from "../store";

currency.prototype.toPrintable = function() {
  const formattedPrice = new Intl.NumberFormat(store.getters.LANGUAGE_CODE, {
    style: "decimal",
    minimumFractionDigits: 2
  }).format(this.value);

  return formattedPrice;
};

export function annotateProductVatBases(
  product,
  count,
  paid,
  originalMenuItemPrice
) {
  const grossNoDiscounts = currency(
    productPriceWithoutDeposit(product, false, [], 0)
  ).multiply(count);
  const grossBasePrice = originalMenuItemPrice
    ? currency(originalMenuItemPrice)
    : grossNoDiscounts;
  const grossPaid = currency(paid);

  product.vatDetails = {
    grossNoDiscounts,
    grossBasePrice,
    grossPaid,
    depositPaid: currency(product.deposit || 0).multiply(count),
    vatPercent: String(
      currency(
        currency(product.vat)
          .multiply(100)
          .subtract(100),
        { precision: 1 }
      )
    )
  };
}

export function annotateMenuVatBases(menu, count, grossPaid) {
  const originalMenuPrice = comboPrice(menu, false, []) - comboDeposit(menu);

  const productsWithoutMenuPrice = menu.components.reduce(
    (acc, mc) => productPriceWithoutDeposit(mc.selected, false, [], 1) + acc,
    0.0
  );

  const itemsWithPaidAndBase = menu.components.map(mc => {
    const menuItemPaidPrice = currency(
      productPriceWithoutDeposit(mc.selected, false, [], 1)
    )
      .multiply(grossPaid)
      .divide(productsWithoutMenuPrice);

    const menuItemBasePrice = currency(
      productPriceWithoutDeposit(mc.selected, false, [], 1)
    )
      .multiply(originalMenuPrice * count)
      .divide(productsWithoutMenuPrice);

    return [mc.selected, menuItemPaidPrice, menuItemBasePrice];
  });

  const itemPaidPricesDelta = itemsWithPaidAndBase
    .reduce(
      (acc, [item, menuItemPaidPrice, menuItemBasePrice]) =>
        acc.add(menuItemPaidPrice),
      currency(0)
    )
    .subtract(grossPaid)
    .distribute(menu.components.length);

  const itemBaseDelta = itemsWithPaidAndBase
    .reduce(
      (acc, [item, menuItemPaidPrice, menuItemBasePrice]) =>
        acc.add(menuItemBasePrice),
      currency(0)
    )
    .subtract(originalMenuPrice * count)
    .distribute(menu.components.length);

  itemsWithPaidAndBase.forEach(
    ([item, menuItemPaidPrice, menuItemBasePrice], index) =>
      annotateProductVatBases(
        item,
        count,
        menuItemPaidPrice.subtract(itemPaidPricesDelta[index]),
        menuItemBasePrice.subtract(itemBaseDelta[index])
      )
  );

  menu.vatDetails = getVatBaseSubtotals([menu]);
}

export function getVatBaseSubtotals(basketItems) {
  const vatDetails = {
    grossNoDiscounts: currency(0),
    grossBasePrice: currency(0),
    grossBaseDiscount: currency(0),
    grossPaid: currency(0),
    depositPaid: currency(0)
  };

  function sumBasketTotals(itemVatDetails) {
    vatDetails.grossNoDiscounts = vatDetails.grossNoDiscounts.add(
      itemVatDetails.grossNoDiscounts
    );
    vatDetails.grossBasePrice = vatDetails.grossBasePrice.add(
      itemVatDetails.grossBasePrice
    );
    vatDetails.grossBaseDiscount = vatDetails.grossBaseDiscount.add(
      itemVatDetails.grossBaseDiscount
    );
    vatDetails.grossPaid = vatDetails.grossPaid.add(itemVatDetails.grossPaid);
    vatDetails.depositPaid = vatDetails.depositPaid.add(
      itemVatDetails.depositPaid
    );
  }

  basketItems.forEach(bi => {
    if (isCombo(bi)) {
      bi.components.forEach(mc => {
        sumBasketTotals(mc.selected.vatDetails);
      });
    } else {
      sumBasketTotals(bi.vatDetails);
    }
  });

  return vatDetails;
}

export function finalizeBasketVatDetails(basket, global_coupon) {
  const lineItems = [];

  basket.forEach(bi => {
    if (isCombo(bi)) {
      bi.components.forEach(mc => {
        const clonedItem = cloneDeep(mc.selected);

        clonedItem.count = clonedItem.count * bi.count;
        clonedItem.menu = bi.uid;
        clonedItem.menu_id = bi.id;
        clonedItem.menu_name = bi.name;
        clonedItem.menu_component = mc.name;

        lineItems.push(clonedItem);
      });
    } else {
      lineItems.push(cloneDeep(bi));
    }
  });

  const lineItemsGrossSubtotal = lineItems.reduce(
    (total, bi) => total.add(bi.vatDetails.grossPaid),
    currency(0)
  );

  const amountOffVoucherDiscount =
    global_coupon &&
    global_coupon[0] &&
    global_coupon[0].data.c === COUPON_CATEGORIES.AMOUNT_OFF
      ? currency(
          Math.min(global_coupon[0].data.a, lineItemsGrossSubtotal.value)
        )
      : currency(0);

  if (amountOffVoucherDiscount && amountOffVoucherDiscount.value > 0) {
    const targetTotal = lineItemsGrossSubtotal.subtract(
      amountOffVoucherDiscount
    );

    lineItems.forEach(item => {
      const lineItemDiscount = amountOffVoucherDiscount
        .multiply(item.vatDetails.grossPaid)
        .divide(lineItemsGrossSubtotal);
      item.vatDetails.grossPaid = item.vatDetails.grossPaid.subtract(
        lineItemDiscount
      );
    });

    const lineItemsDiscountedGrossSubtotalRoundingErrors = lineItems
      .reduce((total, bi) => total.add(bi.vatDetails.grossPaid), currency(0))
      .subtract(targetTotal)
      .distribute(lineItems.length);

    lineItems.forEach((item, index) => {
      const lineItemDiscountError =
        lineItemsDiscountedGrossSubtotalRoundingErrors[index];
      item.vatDetails.grossPaid = item.vatDetails.grossPaid.subtract(
        lineItemDiscountError
      );
    });
  }

  lineItems.forEach(item => {
    item.vatDetails.grossTotalDiscount = item.vatDetails.grossNoDiscounts.subtract(
      item.vatDetails.grossPaid
    );
    item.vatDetails.grossBaseDiscount = item.vatDetails.grossBasePrice.subtract(
      item.vatDetails.grossPaid
    );
    item.vatDetails.vatPaid = item.vatDetails.grossPaid
      .divide(item.vat)
      .multiply(currency(item.vat).subtract(1));

    item.vatDetails.netPaid = item.vatDetails.grossPaid.subtract(
      item.vatDetails.vatPaid
    );
  });

  function reduceLineItemVatDetails(key) {
    return lineItems.reduce((acc, item) => {
      return acc.add(item.vatDetails[key]);
    }, currency(0));
  }

  const vatCategories = {};

  lineItems.forEach(item => {
    vatCategories[item.vatDetails.vatPercent] = vatCategories[
      item.vatDetails.vatPercent
    ] || {
      vatPercent: item.vatDetails.vatPercent,
      grossPaid: currency(0),
      vatPaid: currency(0),
      netPaid: currency(0)
    };
    vatCategories[item.vatDetails.vatPercent].grossPaid = vatCategories[
      item.vatDetails.vatPercent
    ].grossPaid.add(item.vatDetails.grossPaid);
    vatCategories[item.vatDetails.vatPercent].vatPaid = vatCategories[
      item.vatDetails.vatPercent
    ].vatPaid.add(item.vatDetails.vatPaid);
    vatCategories[item.vatDetails.vatPercent].netPaid = vatCategories[
      item.vatDetails.vatPercent
    ].netPaid.add(item.vatDetails.netPaid);
  });

  const grossNoDiscounts = reduceLineItemVatDetails("grossNoDiscounts");
  const grossBasePrice = reduceLineItemVatDetails("grossBasePrice");
  const grossBaseDiscount = reduceLineItemVatDetails("grossBaseDiscount");
  const grossTotalDiscount = reduceLineItemVatDetails("grossTotalDiscount");
  const grossPaid = reduceLineItemVatDetails("grossPaid");
  const netPaid = reduceLineItemVatDetails("netPaid");
  const vatPaid = reduceLineItemVatDetails("vatPaid");
  const depositPaid = reduceLineItemVatDetails("depositPaid");

  const absoluteTotal = currency(
    Math.max(grossPaid.add(depositPaid).value, 0.01)
  );

  const vatDetails = {
    amountOffVoucherDiscount,
    grossNoDiscounts,
    grossBasePrice,
    grossBaseDiscount,
    grossTotalDiscount,
    grossPaid,
    netPaid,
    vatPaid,
    depositPaid,
    vatCategories,
    absoluteTotal
  };

  return { lineItems, vatDetails, global_coupon };
}

export function getReceiptPrototype(basket, coupons) {
  console.warn("lineItems:", basket.lineItems);
  console.warn("global coupons:", coupons);
  console.error("vatDetails:", JSON.stringify(basket.vatDetails, null, 2));

  const receipt = [];

  function adReceiptLine(count, name, price, vatPercent) {
    let line = count ? (String(count) + "x").padEnd(3) : "-  ";
    line += name.padEnd(22);
    line += String(vatPercent || "").padStart(6) + (vatPercent ? " %" : "  ");
    line += String(price).padStart(6) + " €";

    receipt.push(line);
  }

  basket.forEach(bi => {
    if (isCombo(bi)) {
      adReceiptLine(bi.count, bi.name, bi.vatDetails.grossPaid);

      bi.components.forEach(mc => {
        adReceiptLine(
          0,
          mc.selected.name,
          mc.selected.vatDetails.grossPaid,
          mc.selected.vatDetails.vatPercent
        );
      });
    } else {
      adReceiptLine(
        bi.count,
        bi.name,
        bi.vatDetails.grossPaid,
        bi.vatDetails.vatPercent
      );
    }
  });

  receipt.push("");

  if (basket.vatDetails.amountOffVoucherDiscount.value > 0) {
    receipt.push(
      "Discount:".padEnd(31) +
        (
          "-" +
          String(basket.vatDetails.amountOffVoucherDiscount) +
          " €"
        ).padStart(10)
    );
  }

  receipt.push(
    "Subtotal:".padEnd(31) +
      (String(basket.vatDetails.grossPaid) + " €").padStart(10)
  );

  receipt.push("Vat included:");

  Object.values(
    basket.vatDetails.vatCategories
  ).forEach(({ vatPercent, grossPaid, vatPaid }) =>
    receipt.push(
      ("-" + vatPercent.padStart(5) + "% on " + grossPaid + "€").padEnd(33) +
        String(vatPaid).padStart(6) +
        " €"
    )
  );

  receipt.push("");

  receipt.push("Totals:");
  receipt.push(
    "- gross Paid:" + String(basket.vatDetails.grossPaid).padStart(26) + " €"
  );
  receipt.push(
    "- net Paid:  " + String(basket.vatDetails.netPaid).padStart(26) + " €"
  );
  receipt.push(
    "- vat Paid:  " + String(basket.vatDetails.vatPaid).padStart(26) + " €"
  );
  receipt.push(
    "- Deposit:   " + String(basket.vatDetails.depositPaid).padStart(26) + " €"
  );
  receipt.push(
    "- Total:     " +
      String(basket.vatDetails.absoluteTotal).padStart(26) +
      " €"
  );

  receipt.forEach(i => console.log(i));

  return receipt;
}
